import React, { useMemo, useState } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import axios from 'axios'
import { toast } from 'react-toastify'
import { BiDownload, BiPrinter, BiChevronDown, BiChevronRight } from 'react-icons/bi'
import { getAxiosDefaultConfig } from 'utils'
import { scheduledTasksUrl } from 'urls'
import { resolveScheduledTaskDisplaySummary } from './resolveScheduledTaskDisplaySummary'
import {
  extractAllTablesFromResponseData,
  extractConfigTablesFromResponseData,
  extractRunHighlightMetrics,
  extractTableRowsFromResponseData,
  formatLabel,
  formatRunDuration,
  formatValue,
  isConfigOrPaginationTable,
  tableColumnsFromRows,
  unwrapResponseData,
} from '../utils/runResultDisplay'
import { planStepPrimaryCopy, friendlyToolTitle } from '../utils/taskPlanDisplay'
import RunHistoryOverflow from './RunHistoryOverflow'

const MARKDOWN_CLASS =
  'tw-text-sm tw-text-slate-800 tw-leading-relaxed tw-prose tw-prose-sm tw-max-w-none [&_h1]:tw-text-base [&_h1]:tw-font-semibold [&_h2]:tw-text-sm [&_h2]:tw-font-semibold [&_h3]:tw-text-sm [&_h3]:tw-font-medium [&_ul]:tw-list-disc [&_ul]:tw-pl-5 [&_ol]:tw-list-decimal [&_ol]:tw-pl-5 [&_p]:tw-my-2 [&_li]:tw-my-0.5 [&_code]:tw-bg-slate-100 [&_code]:tw-px-1 [&_code]:tw-py-0.5 [&_code]:tw-rounded [&_code]:tw-text-[13px] [&_pre]:tw-bg-slate-100 [&_pre]:tw-p-3 [&_pre]:tw-rounded-lg [&_pre]:tw-overflow-x-auto [&_a]:tw-text-blue-600 [&_a]:tw-underline [&_blockquote]:tw-border-l-4 [&_blockquote]:tw-border-slate-300 [&_blockquote]:tw-pl-3 [&_blockquote]:tw-text-slate-600 [&_table]:tw-text-xs [&_th]:tw-border [&_td]:tw-border [&_th]:tw-px-2 [&_td]:tw-px-2 [&_th]:tw-bg-slate-100'

function stripLeadingSummaryHeading(markdown: string): string {
  return markdown
    .replace(/^#{1,3}\s*(?:executive\s+)?(?:summary|overview|report)\s*\n+/i, '')
    .trim()
}

function ReportMarkdown({ markdown }: { markdown: string }) {
  const source = stripLeadingSummaryHeading(markdown)
  return (
    <div className="tw-max-w-none">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          h1: ({ children }) => (
            <h2 className="tw-mt-0 tw-mb-3 tw-text-lg tw-font-semibold tw-tracking-tight tw-text-slate-900">
              {children}
            </h2>
          ),
          h2: ({ children }) => (
            <h3 className="tw-mt-7 tw-mb-2 tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-[0.14em] tw-text-slate-500">
              {children}
            </h3>
          ),
          h3: ({ children }) => (
            <h3 className="tw-mt-7 tw-mb-2 tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-[0.14em] tw-text-slate-500">
              {children}
            </h3>
          ),
          p: ({ children }) => (
            <p className="tw-my-3 tw-text-[15px] tw-leading-7 tw-text-slate-700 first:tw-mt-0">
              {children}
            </p>
          ),
          strong: ({ children }) => (
            <strong className="tw-font-semibold tw-text-slate-900">{children}</strong>
          ),
          ul: ({ children }) => (
            <ul className="tw-my-3 tw-list-disc tw-space-y-1.5 tw-pl-5 tw-text-[15px] tw-leading-7 tw-text-slate-700">
              {children}
            </ul>
          ),
          ol: ({ children }) => (
            <ol className="tw-my-3 tw-list-decimal tw-space-y-2 tw-pl-5 tw-text-[15px] tw-leading-7 tw-text-slate-700">
              {children}
            </ol>
          ),
          li: ({ children }) => <li className="tw-pl-0.5">{children}</li>,
          blockquote: ({ children }) => (
            <blockquote className="tw-my-4 tw-border-l-2 tw-border-slate-300 tw-pl-4 tw-text-[15px] tw-leading-7 tw-text-slate-600">
              {children}
            </blockquote>
          ),
          table: ({ children }) => (
            <div className="tw-my-4 tw-overflow-x-auto tw-rounded-lg tw-border tw-border-slate-200">
              <table className="tw-mb-0 tw-w-full tw-border-collapse tw-text-sm">{children}</table>
            </div>
          ),
          thead: ({ children }) => <thead className="tw-bg-slate-50">{children}</thead>,
          th: ({ children }) => (
            <th className="tw-px-3 tw-py-2 tw-text-left tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-wide tw-text-slate-500">
              {children}
            </th>
          ),
          td: ({ children }) => (
            <td className="tw-border-t tw-border-slate-100 tw-px-3 tw-py-2.5 tw-text-slate-800">
              {children}
            </td>
          ),
          hr: () => <hr className="tw-my-6 tw-border-slate-200" />,
          a: ({ href, children }) => (
            <a href={href} className="tw-text-slate-900 tw-underline tw-underline-offset-2">
              {children}
            </a>
          ),
        }}
      >
        {source}
      </ReactMarkdown>
    </div>
  )
}

type Props = {
  responseData: any
  resolvedParams?: any
  run?: any
  taskId?: string
  taskName?: string
  errorMessage?: string | null
  runs?: any[]
  selectedRunId?: string | null
  onSelectRun?: (runId: string) => void
  afterSummary?: React.ReactNode
}

function filenameFromContentDisposition(header: string | undefined, fallback: string): string {
  if (!header) return fallback
  const match = /filename\*?=(?:UTF-8''|")?([^\";]+)"?/i.exec(header)
  if (!match?.[1]) return fallback
  try {
    return decodeURIComponent(match[1].replace(/"/g, '').trim())
  } catch {
    return match[1].replace(/"/g, '').trim() || fallback
  }
}

async function downloadRunExportBlob(url: string, fallbackFilename: string) {
  const res = await axios.get(url, {
    ...getAxiosDefaultConfig(),
    responseType: 'blob',
  })
  const filename = filenameFromContentDisposition(
    res.headers?.['content-disposition'],
    fallbackFilename,
  )
  const blob = new Blob([res.data], {
    type: res.headers?.['content-type'] || 'application/octet-stream',
  })
  const objectUrl = window.URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = objectUrl
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  window.URL.revokeObjectURL(objectUrl)
}

function ResultTable({ rows }: { rows: Record<string, any>[] }) {
  const MAX_ROWS = 50
  const columns = tableColumnsFromRows(rows, 20)
  if (columns.length === 0) return null
  const displayRows = rows.slice(0, MAX_ROWS)

  return (
    <div className="tw-overflow-x-auto tw-rounded-xl tw-border tw-border-slate-200 tw-bg-white tw-shadow-sm">
      <table className="tw-w-full tw-text-sm">
        <thead>
          <tr className="tw-bg-slate-50 tw-border-b tw-border-slate-200">
            {columns.map((col) => (
              <th
                key={col}
                className="tw-px-4 tw-py-3 tw-text-left tw-font-semibold tw-text-slate-600 tw-whitespace-nowrap"
              >
                {formatLabel(col)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {displayRows.map((row, i) => (
            <tr
              key={i}
              className="tw-border-b tw-border-slate-100 last:tw-border-b-0 hover:tw-bg-slate-50/70"
            >
              {columns.map((col) => (
                <td key={col} className="tw-px-4 tw-py-2.5 tw-text-slate-700">
                  {formatValue(row[col], col)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      <div className="tw-text-xs tw-text-slate-500 tw-px-4 tw-py-2 tw-border-t tw-border-slate-100 tw-bg-slate-50/50">
        {rows.length > MAX_ROWS
          ? `Showing first ${MAX_ROWS} of ${rows.length.toLocaleString()} rows`
          : `${rows.length.toLocaleString()} row${rows.length === 1 ? '' : 's'}`}
      </div>
    </div>
  )
}

function AgentStepsTimeline({
  steps,
  useAgentSteps,
  totalIterations,
}: {
  steps: any[]
  useAgentSteps: boolean
  totalIterations?: number
}) {
  const [techOpen, setTechOpen] = useState(false)
  if (!steps.length) return null

  return (
    <div className="tw-space-y-2">
      <div className="tw-text-xs tw-font-medium tw-text-slate-500">
        What happened
        <span className="tw-ml-1 tw-font-normal tw-text-slate-400">
          {useAgentSteps
            ? `${steps.length} step${steps.length === 1 ? '' : 's'}${
                totalIterations != null ? ` · ${totalIterations} turn${totalIterations === 1 ? '' : 's'}` : ''
              }`
            : `${steps.length} tool step${steps.length === 1 ? '' : 's'}`}
        </span>
      </div>
      {useAgentSteps
        ? steps.map((s: any, i: number) => {
            const failed = s.success === false
            const label = planStepPrimaryCopy({
              tool_name: s.tool_name,
              display_description: null,
              tool_parameters: s.tool_input,
            })
            return (
              <div
                key={i}
                className={`tw-rounded-md tw-border tw-px-3 tw-py-2 ${
                  failed
                    ? 'tw-border-red-200 tw-bg-red-50/70'
                    : 'tw-border-slate-200 tw-bg-slate-50/80'
                }`}
              >
                <div className="tw-flex tw-items-start tw-gap-2">
                  <span
                    className={`tw-mt-0.5 tw-h-2 tw-w-2 tw-shrink-0 tw-rounded-full ${
                      failed ? 'tw-bg-red-500' : 'tw-bg-emerald-500'
                    }`}
                  />
                  <div className="tw-min-w-0">
                    <p className="tw-m-0 tw-text-sm tw-font-medium tw-text-slate-800">
                      {friendlyToolTitle(s.tool_name) || label}
                      {failed ? ' — failed' : ''}
                    </p>
                    <p className="tw-m-0 tw-mt-0.5 tw-font-mono tw-text-[11px] tw-text-slate-500">
                      Turn {s.iteration ?? i + 1}: {String(s.tool_name || 'tool').replace(/^mcp__payhelm__/i, '')}
                    </p>
                  </div>
                </div>
              </div>
            )
          })
        : steps.map((s: any, i: number) => (
            <div key={i} className="tw-text-sm tw-text-slate-700">
              Step {s.execution_order ?? i + 1}: {friendlyToolTitle(s.tool_name)} — success
            </div>
          ))}
      <button
        type="button"
        onClick={() => setTechOpen((v) => !v)}
        className="tw-border-0 tw-bg-transparent tw-p-0 tw-text-[11px] tw-font-medium tw-text-slate-500 hover:tw-text-slate-700 tw-cursor-pointer"
      >
        {techOpen ? 'Hide technical details' : 'Show technical details'}
      </button>
      {techOpen && (
        <pre className="tw-m-0 tw-max-h-48 tw-overflow-auto tw-rounded-md tw-bg-slate-100 tw-p-2 tw-text-[11px] tw-text-slate-700">
          {JSON.stringify(steps, null, 2)}
        </pre>
      )}
    </div>
  )
}

function CollapsedResults({
  overviewBits,
  truncatedExport,
  headlineTable,
  highlightMetrics,
}: {
  overviewBits: string[]
  truncatedExport: boolean
  headlineTable: { id: string; tool_name: string; rows: Record<string, any>[] } | null
  highlightMetrics: { key: string; label: string; value: string }[]
}) {
  const [open, setOpen] = useState(false)
  if (!headlineTable && overviewBits.length === 0 && highlightMetrics.length === 0 && !truncatedExport) {
    return null
  }
  const rowHint = headlineTable?.rows?.length
    ? `${headlineTable.rows.length.toLocaleString()} row${headlineTable.rows.length === 1 ? '' : 's'}`
    : null

  return (
    <div className="tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white tw-overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="tw-flex tw-w-full tw-items-center tw-gap-2 tw-px-3 tw-py-2.5 tw-text-left hover:tw-bg-slate-50"
      >
        {open ? (
          <BiChevronDown className="tw-h-4 tw-w-4 tw-text-slate-400" />
        ) : (
          <BiChevronRight className="tw-h-4 tw-w-4 tw-text-slate-400" />
        )}
        <span className="tw-text-xs tw-font-medium tw-text-slate-500">Results</span>
        {rowHint ? <span className="tw-text-xs tw-text-slate-400">{rowHint}</span> : null}
      </button>
      {open && (
        <div className="tw-border-t tw-border-slate-100 tw-px-3 tw-py-3 tw-space-y-3">
          {(overviewBits.length > 0 || truncatedExport) && (
            <div className="tw-flex tw-flex-wrap tw-gap-1.5">
              {overviewBits.map((bit) => (
                <span
                  key={bit}
                  className="tw-inline-flex tw-rounded-full tw-bg-slate-100 tw-px-2.5 tw-py-0.5 tw-text-[11px] tw-font-medium tw-text-slate-600"
                >
                  {bit}
                </span>
              ))}
              {truncatedExport && (
                <span className="tw-inline-flex tw-rounded-full tw-bg-amber-50 tw-border tw-border-amber-200 tw-px-2.5 tw-py-0.5 tw-text-[11px] tw-font-medium tw-text-amber-800">
                  CSV truncated to 10k rows
                </span>
              )}
            </div>
          )}
          {headlineTable && (
            <div>
              <div className="tw-mb-2 tw-text-xs tw-font-medium tw-text-slate-500">
                {!headlineTable.tool_name || headlineTable.tool_name === 'data'
                  ? 'Results'
                  : friendlyToolTitle(headlineTable.tool_name)}
              </div>
              <ResultTable rows={headlineTable.rows} />
            </div>
          )}
          {highlightMetrics.length > 0 && (
            <div className="tw-grid tw-grid-cols-2 tw-gap-2 sm:tw-grid-cols-4">
              {highlightMetrics.map((m) => (
                <div
                  key={m.key}
                  className="tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white tw-p-3 tw-shadow-sm"
                >
                  <div className="tw-mb-0.5 tw-text-xs tw-font-medium tw-text-slate-500">{m.label}</div>
                  <div className="tw-text-lg tw-font-semibold tw-text-slate-800">{m.value}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function summaryPlaceholder(opts: {
  status?: string
  errorMessage?: string | null
  rowCount?: number
  hasWrapUp?: boolean
}): { label: string; body: string; chipClass: string } {
  if (opts.hasWrapUp) {
    return {
      label: 'Complete',
      body: '',
      chipClass: 'tw-bg-emerald-50 tw-text-emerald-800',
    }
  }
  if (opts.status === 'running') {
    return {
      label: 'In progress',
      body: 'The run is still working. A wrap-up will show here when it finishes.',
      chipClass: 'tw-bg-blue-50 tw-text-blue-800',
    }
  }
  if (opts.errorMessage) {
    return {
      label: 'Unavailable',
      body: 'This run did not produce a wrap-up. See the error above.',
      chipClass: 'tw-bg-slate-100 tw-text-slate-700',
    }
  }
  if (opts.rowCount && opts.rowCount > 0) {
    return {
      label: 'Wrap-up not saved',
      body: `This run completed and captured ${opts.rowCount.toLocaleString()} row${
        opts.rowCount === 1 ? '' : 's'
      }. A written wrap-up was not saved — open Results below for the data.`,
      chipClass: 'tw-bg-amber-50 tw-text-amber-800',
    }
  }
  return {
    label: 'Wrap-up not saved',
    body: 'This run completed, but a written wrap-up was not saved. Open Results or Run details for what was captured.',
    chipClass: 'tw-bg-amber-50 tw-text-amber-800',
  }
}

function SummarySectionHeader({
  chipLabel,
  chipClass,
  actions,
  taskName,
  metaLine,
}: {
  chipLabel: string
  chipClass: string
  actions: React.ReactNode
  taskName?: string
  metaLine?: string | null
}) {
  return (
    <div className="tw-mb-5 tw-flex tw-flex-wrap tw-items-start tw-justify-between tw-gap-3 tw-border-b tw-border-slate-100 tw-pb-4">
      <div className="tw-min-w-0">
        <div className="tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-[0.16em] tw-text-slate-400">
          Executive summary
        </div>
        {taskName ? (
          <h2 className="tw-m-0 tw-mt-1.5 tw-text-xl tw-font-semibold tw-leading-snug tw-text-slate-900">
            {taskName}
          </h2>
        ) : (
          <h2 className="tw-m-0 tw-mt-1.5 tw-text-xl tw-font-semibold tw-leading-snug tw-text-slate-900">
            Summary
          </h2>
        )}
        {metaLine ? (
          <p className="tw-m-0 tw-mt-1 tw-text-xs tw-text-slate-500">{metaLine}</p>
        ) : null}
      </div>
      <div className="tw-flex tw-shrink-0 tw-flex-wrap tw-items-center tw-gap-2">
        {actions}
        <span
          className={`tw-inline-flex tw-rounded-full tw-px-2.5 tw-py-0.5 tw-text-[11px] tw-font-medium ${chipClass}`}
        >
          {chipLabel}
        </span>
      </div>
    </div>
  )
}

function RunDetailsSection({
  leftoverTables,
  configTables,
  steps,
  useAgentSteps,
  totalIterations,
}: {
  leftoverTables: { id: string; tool_name: string; rows: Record<string, any>[] }[]
  configTables: { id: string; tool_name: string; rows: Record<string, any>[] }[]
  steps: any[]
  useAgentSteps: boolean
  totalIterations?: number
}) {
  const [open, setOpen] = useState(false)
  const extraCount = leftoverTables.length + configTables.length
  if (!steps.length && extraCount === 0) return null

  return (
    <div className="tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white tw-overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="tw-flex tw-w-full tw-items-center tw-gap-2 tw-px-3 tw-py-2.5 tw-text-left hover:tw-bg-slate-50"
      >
        {open ? (
          <BiChevronDown className="tw-h-4 tw-w-4 tw-text-slate-400" />
        ) : (
          <BiChevronRight className="tw-h-4 tw-w-4 tw-text-slate-400" />
        )}
        <span className="tw-text-xs tw-font-medium tw-text-slate-500">Run details</span>
        <span className="tw-text-xs tw-text-slate-400">
          {steps.length > 0
            ? `${steps.length} step${steps.length === 1 ? '' : 's'}`
            : 'Tool and config'}
          {extraCount > 0 ? ` · ${extraCount} extra table${extraCount === 1 ? '' : 's'}` : ''}
        </span>
      </button>
      {open && (
        <div className="tw-border-t tw-border-slate-100 tw-px-3 tw-py-3 tw-space-y-4">
          {leftoverTables.map((table, index) => (
            <div key={table.id || `left-${index}`}>
              <div className="tw-mb-2 tw-text-xs tw-font-medium tw-text-slate-500">
                {friendlyToolTitle(table.tool_name)}
              </div>
              <ResultTable rows={table.rows} />
            </div>
          ))}
          {configTables.map((table, index) => (
            <div key={table.id || `cfg-${index}`}>
              <div className="tw-mb-2 tw-text-xs tw-font-medium tw-text-slate-500">
                {friendlyToolTitle(table.tool_name)} (config)
              </div>
              <ResultTable rows={table.rows} />
            </div>
          ))}
          <AgentStepsTimeline
            steps={steps}
            useAgentSteps={useAgentSteps}
            totalIterations={totalIterations}
          />
        </div>
      )}
    </div>
  )
}

/** Formatted expanded-run result with server-backed CSV/PDF export. */
export default function RunResultPanel({
  responseData,
  run,
  taskId,
  taskName,
  errorMessage,
  runs,
  selectedRunId,
  onSelectRun,
  afterSummary,
}: Props) {
  const [exporting, setExporting] = useState<'csv' | 'pdf' | null>(null)
  const legacySteps =
    responseData && typeof responseData === 'object' && Array.isArray(responseData.steps)
      ? responseData.steps
      : []
  const agentSteps =
    responseData && typeof responseData === 'object' && Array.isArray(responseData.agent_steps)
      ? responseData.agent_steps
      : []
  const useAgentSteps = agentSteps.length > 0
  const steps = useAgentSteps ? agentSteps : legacySteps
  const finalResponse = resolveScheduledTaskDisplaySummary(responseData)
  const totalIterations = responseData?.total_iterations
  const exportArtifact =
    responseData && typeof responseData === 'object' && responseData.export
      ? responseData.export
      : null
  const tables = useMemo(() => extractAllTablesFromResponseData(responseData), [responseData])
  const configTables = useMemo(
    () => extractConfigTablesFromResponseData(responseData),
    [responseData],
  )
  const tableRows = useMemo(() => {
    if (tables[0]?.rows?.length) return tables[0].rows
    if (exportArtifact?.primary?.rows?.length) {
      const primaryRows = exportArtifact.primary.rows as Record<string, any>[]
      if (!isConfigOrPaginationTable(primaryRows)) return primaryRows
    }
    return extractTableRowsFromResponseData(responseData)
  }, [responseData, exportArtifact, tables])
  const headlineTable =
    tables[0] ||
    (tableRows.length > 0 ? { id: 'data', tool_name: 'data', rows: tableRows } : null)
  const leftoverTables = tables.slice(1)
  const hasTable = Boolean(headlineTable?.rows?.length)
  const highlightMetrics = useMemo(
    () => extractRunHighlightMetrics(headlineTable ? [headlineTable] : []),
    [headlineTable],
  )
  const canCsv =
    Boolean(taskId && run?.id) &&
    hasTable &&
    !isConfigOrPaginationTable(headlineTable?.rows || []) &&
    (exportArtifact
      ? Boolean(exportArtifact.primary?.rows?.length) &&
        !isConfigOrPaginationTable(exportArtifact.primary.rows as Record<string, any>[])
      : true)
  const canPdf = Boolean(taskId && run?.id) && Boolean(finalResponse || hasTable || errorMessage)
  const truncatedExport = Boolean(exportArtifact?.primary?.truncated)

  const duration = formatRunDuration(run?.started_at, run?.completed_at)

  const handleCsv = async () => {
    if (!taskId || !run?.id) {
      toast.info('Export is unavailable for this run')
      return
    }
    if (!canCsv) {
      toast.info('This run has no table data to export')
      return
    }
    setExporting('csv')
    try {
      await downloadRunExportBlob(
        `${scheduledTasksUrl}/${encodeURIComponent(taskId)}/runs/${encodeURIComponent(run.id)}/export.csv`,
        'task-run.csv',
      )
      toast.success('CSV downloaded')
    } catch (err: any) {
      const status = err?.response?.status
      if (status === 404) toast.info('This run has no table data to export')
      else toast.error('Failed to download CSV')
    } finally {
      setExporting(null)
    }
  }

  const handlePdf = async () => {
    if (!taskId || !run?.id) {
      toast.info('Export is unavailable for this run')
      return
    }
    setExporting('pdf')
    try {
      await downloadRunExportBlob(
        `${scheduledTasksUrl}/${encodeURIComponent(taskId)}/runs/${encodeURIComponent(run.id)}/export.pdf`,
        'task-run.pdf',
      )
      toast.success('PDF downloaded')
    } catch (err: any) {
      const status = err?.response?.status
      if (status === 404) toast.info('This run has no content to export as PDF')
      else toast.error('Failed to download PDF')
    } finally {
      setExporting(null)
    }
  }

  const overviewBits: string[] = []
  if (run?.status) overviewBits.push(String(run.status))
  if (run?.trigger_type) overviewBits.push(run.trigger_type === 'manual' ? 'Manual' : 'Scheduled')
  if (run?.started_at) overviewBits.push(new Date(run.started_at).toLocaleString())
  if (duration) overviewBits.push(`Duration ${duration}`)
  if (run?.delivery_status) overviewBits.push(`Delivery: ${run.delivery_status}`)
  if (totalIterations != null) overviewBits.push(`${totalIterations} AI turn${totalIterations === 1 ? '' : 's'}`)

  const placeholder = summaryPlaceholder({
    status: run?.status,
    errorMessage,
    rowCount: headlineTable?.rows?.length,
    hasWrapUp: finalResponse.length > 0,
  })

  const metaLine = [
    run?.started_at ? new Date(run.started_at).toLocaleString() : null,
    duration,
    run?.trigger_type === 'manual' ? 'Manual run' : run?.trigger_type ? 'Scheduled run' : null,
  ]
    .filter(Boolean)
    .join(' · ')

  const exportActions = (
    <div className="tw-flex tw-items-center tw-gap-1.5">
      {runs && onSelectRun ? (
        <RunHistoryOverflow
          runs={runs}
          selectedRunId={selectedRunId ?? null}
          onSelectRun={onSelectRun}
        />
      ) : null}
      <button
        type="button"
        onClick={handleCsv}
        disabled={!canCsv || exporting !== null}
        className="tw-inline-flex tw-items-center tw-gap-1 tw-rounded-md tw-border tw-border-slate-200 tw-bg-white tw-px-2.5 tw-py-1.5 tw-text-xs tw-font-medium tw-text-slate-700 hover:tw-bg-slate-50 disabled:tw-opacity-40 disabled:tw-cursor-not-allowed"
        title={canCsv ? 'Download table as CSV' : 'No table data'}
      >
        <BiDownload className="tw-h-3.5 tw-w-3.5" aria-hidden />
        {exporting === 'csv' ? 'CSV…' : 'CSV'}
      </button>
      <button
        type="button"
        onClick={handlePdf}
        disabled={!canPdf || exporting !== null}
        className="tw-inline-flex tw-items-center tw-gap-1 tw-rounded-md tw-border tw-border-slate-200 tw-bg-white tw-px-2.5 tw-py-1.5 tw-text-xs tw-font-medium tw-text-slate-700 hover:tw-bg-slate-50 disabled:tw-opacity-40 disabled:tw-cursor-not-allowed"
        title="Download PDF snapshot"
      >
        <BiPrinter className="tw-h-3.5 tw-w-3.5" aria-hidden />
        {exporting === 'pdf' ? 'PDF…' : 'PDF'}
      </button>
    </div>
  )

  if (!responseData && !errorMessage) {
    return (
      <div className="tw-space-y-3">
        <div className="tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white tw-p-3 sm:tw-p-4">
          <SummarySectionHeader
            chipLabel="Unavailable"
            chipClass="tw-bg-slate-100 tw-text-slate-700"
            actions={exportActions}
            taskName={taskName}
            metaLine={metaLine || null}
          />
          <p className="tw-m-0 tw-text-sm tw-leading-relaxed tw-text-slate-600">
            No results for this run yet.
          </p>
        </div>
        {afterSummary}
      </div>
    )
  }

  return (
    <div className="tw-space-y-3">
      {errorMessage && (
        <div className="tw-rounded-lg tw-border tw-border-red-200 tw-bg-red-50 tw-px-3 tw-py-2.5 tw-text-sm tw-text-red-700">
          <div className="tw-mb-1 tw-text-xs tw-font-medium tw-text-red-600">Error</div>
          {errorMessage}
        </div>
      )}

      <div className="tw-rounded-xl tw-border tw-border-slate-200 tw-bg-white tw-p-5 sm:tw-p-6">
        <SummarySectionHeader
          chipLabel={placeholder.label}
          chipClass={placeholder.chipClass}
          actions={exportActions}
          taskName={taskName}
          metaLine={metaLine || null}
        />
        {finalResponse.length > 0 ? (
          <ReportMarkdown markdown={finalResponse} />
        ) : (
          <p className="tw-m-0 tw-text-[15px] tw-leading-7 tw-text-slate-600">{placeholder.body}</p>
        )}
      </div>

      {afterSummary}

      <CollapsedResults
        overviewBits={overviewBits}
        truncatedExport={truncatedExport}
        headlineTable={headlineTable}
        highlightMetrics={highlightMetrics}
      />
      <RunDetailsSection
        leftoverTables={leftoverTables}
        configTables={configTables}
        steps={steps}
        useAgentSteps={useAgentSteps}
        totalIterations={totalIterations}
      />

      {tables.length === 0 && !hasTable && !finalResponse && steps.length === 0 && !errorMessage && (
        <FallbackRaw responseData={responseData} />
      )}
    </div>
  )
}

function FallbackRaw({ responseData }: { responseData: any }) {
  const data = unwrapResponseData(responseData)
  if (data == null) {
    return <div className="tw-text-sm tw-text-slate-500">No response data</div>
  }
  if (typeof data === 'string' && data.length < 500) {
    return (
      <div className={MARKDOWN_CLASS}>
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{data}</ReactMarkdown>
      </div>
    )
  }
  if (data && typeof data === 'object' && !Array.isArray(data) && Object.keys(data).length <= 8) {
    const entries = Object.entries(data).filter(([, v]) => v !== undefined && v !== null)
    if (entries.length > 0) {
      return (
        <div className="tw-space-y-1 tw-text-sm">
          {entries.map(([k, v]) => (
            <div key={k} className="tw-flex tw-gap-2 tw-items-baseline">
              <span className="tw-font-medium tw-text-slate-600">{formatLabel(k)}:</span>
              <span className="tw-text-slate-800">{formatValue(v, k)}</span>
            </div>
          ))}
        </div>
      )
    }
  }
  return (
    <pre className="tw-text-xs tw-bg-slate-50 tw-p-4 tw-rounded-xl tw-overflow-x-auto tw-max-h-96 tw-overflow-y-auto tw-border tw-border-slate-200 tw-text-slate-700">
      {typeof data === 'string' ? data : JSON.stringify(data, null, 2)}
    </pre>
  )
}
